/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type React from 'react';
import { ImageOff } from 'lucide-react';
import { Link } from '@/components/link';
import { DynamicImage } from '@/components/dynamic-image';
import { useAnalytics } from '@/hooks/use-analytics';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';
import type { ProductSuggestion } from './types';

interface SuggestionsProductListProps {
    suggestions?: ProductSuggestion[];
    searchPhrase?: string;
    closeAndNavigate?: (link: string) => void;
}

const SuggestionsProductList: React.FC<SuggestionsProductListProps> = ({
    suggestions,
    searchPhrase,
    closeAndNavigate,
}) => {
    const analytics = useAnalytics();
    const config = useConfig();
    const { t } = useTranslation('common');

    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleClick = (suggestion: ProductSuggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        if (closeAndNavigate) {
            closeAndNavigate(suggestion.link);
        }
    };

    return (
        <div data-testid="sf-vertical-product-suggestions" className="flex flex-col gap-3">
            {suggestions.map((suggestion) => (
                <Link
                    data-testid="product-tile"
                    to={suggestion.link}
                    key={suggestion.link}
                    onMouseDown={() => handleClick(suggestion)}
                    className="flex h-[70px] w-full gap-3 hover:underline">
                    <div className="h-[70px] w-[60px] shrink-0 overflow-hidden rounded-[4px]">
                        {suggestion.image ? (
                            <DynamicImage
                                src={`${toImageUrl({ src: suggestion.image, config })}[?sw={width}]`}
                                alt={suggestion.name || t('productImageAlt')}
                                imageProps={{
                                    className: 'w-full h-full object-cover block',
                                }}
                                loading="eager"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center bg-muted text-header-foreground">
                                <ImageOff className="size-5" />
                            </div>
                        )}
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
                        <p className="line-clamp-2 font-lato text-sm leading-5 font-semibold tracking-normal text-[#51575C]">
                            {suggestion.name}
                        </p>
                    </div>
                </Link>
            ))}
        </div>
    );
};

export default SuggestionsProductList;
